import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Paperclip, X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/layout/PageHeader";
import Reveal from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-panel px-4 py-3.5 text-sm text-cream placeholder:text-ash outline-none transition-colors focus:border-brand";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-fog";

const Field = ({ label, required, optional, children, testid }) => (
  <div data-testid={testid}>
    <label className={labelCls}>
      {label}
      {required && <span className="text-brand"> *</span>}
      {optional && <span className="ml-2 normal-case tracking-normal text-ash">({optional})</span>}
    </label>
    {children}
  </div>
);

const Quote = () => {
  const { t } = useLanguage();
  const f = t.quote.fields;
  const ph = t.quote.placeholders;
  const [form, setForm] = useState({
    name: "", company: "", phone: "", whatsapp: "", email: "",
    city: "", project_type: "", service: "", budget: "", deadline: "", description: "",
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    const valid = picked.filter((file) => allowed.includes(file.type) && file.size <= 10 * 1024 * 1024);
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((file) => data.append("files", file));
      await axios.post(`${API}/quotes`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(t.quote.errorText);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader chapter={t.quote.chapter} title={t.quote.title} subtitle={t.quote.intro} testid="quote-header" />

      <section className="bg-ink py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {sent ? (
            <Reveal>
              <div data-testid="quote-success-panel" className="border border-brand/40 bg-panel p-10 text-center">
                <CheckCircle2 className="mx-auto h-14 w-14 text-brand" strokeWidth={1.25} />
                <h2 className="mt-6 font-display text-3xl font-bold uppercase text-cream">{t.quote.successTitle}</h2>
                <p className="mt-3 text-base text-fog">{t.quote.successText}</p>
                <Link
                  to="/"
                  data-testid="quote-success-home-link"
                  className="mt-8 inline-block bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover"
                >
                  {t.quote.successCta}
                </Link>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="quote-request-form" className="border border-line bg-surface p-6 sm:p-10">
                <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.quote.sections.identity}</h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field label={f.name} required testid="quote-field-name">
                    <input required value={form.name} onChange={set("name")} placeholder={ph.name} className={inputCls} data-testid="quote-input-name" />
                  </Field>
                  <Field label={f.company} optional={f.companyOptional} testid="quote-field-company">
                    <input value={form.company} onChange={set("company")} placeholder={ph.company} className={inputCls} data-testid="quote-input-company" />
                  </Field>
                  <Field label={f.phone} required testid="quote-field-phone">
                    <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={ph.phone} className={inputCls} data-testid="quote-input-phone" />
                  </Field>
                  <Field label={f.whatsapp} testid="quote-field-whatsapp">
                    <input type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder={ph.whatsapp} className={inputCls} data-testid="quote-input-whatsapp" />
                  </Field>
                  <Field label={f.email} required testid="quote-field-email">
                    <input required type="email" value={form.email} onChange={set("email")} placeholder={ph.email} className={inputCls} data-testid="quote-input-email" />
                  </Field>
                  <Field label={f.city} required testid="quote-field-city">
                    <input required value={form.city} onChange={set("city")} placeholder={ph.city} className={inputCls} data-testid="quote-input-city" />
                  </Field>
                </div>

                <h2 className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.quote.sections.project}</h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field label={f.projectType} required testid="quote-field-project-type">
                    <select required value={form.project_type} onChange={set("project_type")} className={inputCls} data-testid="quote-select-project-type">
                      <option value="" disabled>{ph.select}</option>
                      {t.quote.projectTypes.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label={f.service} testid="quote-field-service">
                    <input value={form.service} onChange={set("service")} placeholder={f.service} className={inputCls} data-testid="quote-input-service" />
                  </Field>
                  <Field label={f.budget} testid="quote-field-budget">
                    <select value={form.budget} onChange={set("budget")} className={inputCls} data-testid="quote-select-budget">
                      <option value="">{ph.select}</option>
                      {t.quote.budgets.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label={f.deadline} testid="quote-field-deadline">
                    <select value={form.deadline} onChange={set("deadline")} className={inputCls} data-testid="quote-select-deadline">
                      <option value="">{ph.select}</option>
                      {t.quote.deadlines.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2" data-testid="quote-field-description">
                    <label className={labelCls}>{f.description} <span className="text-brand">*</span></label>
                    <textarea
                      required
                      rows={5}
                      value={form.description}
                      onChange={set("description")}
                      placeholder={ph.description}
                      className={`${inputCls} resize-y`}
                      data-testid="quote-textarea-description"
                    />
                  </div>
                </div>

                <h2 className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-brand">{t.quote.sections.details}</h2>
                <div className="mt-6" data-testid="quote-field-files">
                  <label className={labelCls}>{f.files}</label>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="quote-file-upload-button"
                    className="flex w-full items-center justify-center gap-3 border border-dashed border-line bg-panel px-4 py-8 text-sm text-fog transition-colors hover:border-brand hover:text-brand"
                  >
                    <Paperclip className="h-4 w-4" />
                    {f.filesButton}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={addFiles}
                    className="hidden"
                    data-testid="quote-file-upload-input"
                  />
                  <p className="mt-2 font-mono text-[11px] text-ash">{f.filesHint}</p>
                  {files.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid="quote-files-list">
                      {files.map((file, i) => (
                        <li key={`${file.name}-${i}`} className="flex items-center justify-between border border-line bg-panel px-4 py-2.5">
                          <span className="truncate text-sm text-cream">{file.name}</span>
                          <button
                            type="button"
                            aria-label={`${file.name}`}
                            data-testid={`quote-file-remove-${i}`}
                            onClick={() => setFiles(files.filter((_, j) => j !== i))}
                            className="ml-3 text-ash transition-colors hover:text-brand"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-form-submit-button"
                  className="mt-10 flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover disabled:opacity-60"
                >
                  {sending && <Loader2 className="h-5 w-5 animate-spin" />}
                  {sending ? t.quote.sending : t.quote.submit}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default Quote;
